import { z } from 'zod';
import {
  JIRA_ACTION_PREVIEW_COMMENT_MAX_LENGTH,
  JIRA_ACTION_PREVIEW_RATIONALE_MAX_LENGTH,
  JIRA_ACTION_PREVIEW_SUBTASK_DESCRIPTION_MAX_LENGTH,
  JIRA_ACTION_PREVIEW_SUBTASK_SUMMARY_MAX_LENGTH,
  JIRA_ACTION_PREVIEW_TITLE_MAX_LENGTH,
} from './content-intelligence-recommendation-dto';

export const DEFAULT_CONTENT_INTELLIGENCE_TICKET_QUALITY_LIMIT = 50;
export const MAX_CONTENT_INTELLIGENCE_TICKET_QUALITY_LIMIT = 100;

const factorScoreSchema = z.number().min(0).max(1);
const factorEvidenceSchema = z.array(z.string().trim().min(1).max(500)).max(3);

const contentIntelligenceTicketQualityJiraActionBaseSchema = z.object({
  title: z.string().trim().min(1).max(JIRA_ACTION_PREVIEW_TITLE_MAX_LENGTH),
  rationale: z.string().trim().min(1).max(JIRA_ACTION_PREVIEW_RATIONALE_MAX_LENGTH),
});

export const contentIntelligenceTicketQualityJiraActionSchema = z.discriminatedUnion('operation', [
  contentIntelligenceTicketQualityJiraActionBaseSchema
    .extend({
      operation: z.literal('add_comment'),
      payload: z
        .object({
          commentBody: z.string().trim().min(1).max(JIRA_ACTION_PREVIEW_COMMENT_MAX_LENGTH),
        })
        .strict(),
    })
    .strict(),
  contentIntelligenceTicketQualityJiraActionBaseSchema
    .extend({
      operation: z.literal('create_subtask'),
      payload: z
        .object({
          summary: z.string().trim().min(1).max(JIRA_ACTION_PREVIEW_SUBTASK_SUMMARY_MAX_LENGTH),
          description: z
            .string()
            .trim()
            .min(1)
            .max(JIRA_ACTION_PREVIEW_SUBTASK_DESCRIPTION_MAX_LENGTH),
        })
        .strict(),
    })
    .strict(),
]);

export const contentIntelligenceTicketQualityFactorsSchema = z
  .object({
    clarityOfOutcome: factorScoreSchema,
    acceptanceCriteria: factorScoreSchema,
    scopeBoundaries: factorScoreSchema,
    dependencySignals: factorScoreSchema,
    ambiguityMarkers: factorScoreSchema,
    missingAcceptanceCriteria: z.boolean(),
  })
  .strict();

export const contentIntelligenceTicketQualityFactorEvidenceSchema = z
  .object({
    clarityOfOutcome: factorEvidenceSchema,
    acceptanceCriteria: factorEvidenceSchema,
    scopeBoundaries: factorEvidenceSchema,
    dependencySignals: factorEvidenceSchema,
    ambiguityMarkers: factorEvidenceSchema,
  })
  .strict();

export const contentIntelligenceTicketQualityQuerySchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_TICKET_QUALITY_LIMIT)
    .default(DEFAULT_CONTENT_INTELLIGENCE_TICKET_QUALITY_LIMIT),
});

const contentIntelligenceTicketQualityAssessmentBaseSchema = z.object({
  assessmentId: z.string().min(1),
  itemId: z.number().int().positive(),
  loopId: z.number().int().positive(),
  caveats: z.array(z.string().trim().min(1).max(500)).max(10),
  modelName: z.string().min(1),
  pipelineVersion: z.string().min(1),
  promptVersion: z.string().min(1),
  inputVersion: z.string().min(1),
  itemUpdatedAt: z.iso.datetime().nullable(),
  evaluatedAt: z.iso.datetime(),
});

export const contentIntelligenceTicketQualityAssessedAssessmentSchema =
  contentIntelligenceTicketQualityAssessmentBaseSchema.extend({
    status: z.literal('assessed'),
    score: factorScoreSchema,
    factors: contentIntelligenceTicketQualityFactorsSchema,
    confidence: factorScoreSchema,
    factorEvidence: contentIntelligenceTicketQualityFactorEvidenceSchema,
    jiraAction: contentIntelligenceTicketQualityJiraActionSchema.nullable(),
  });

export const contentIntelligenceTicketQualitySourceUnavailableAssessmentSchema =
  contentIntelligenceTicketQualityAssessmentBaseSchema.extend({
    status: z.literal('source_unavailable'),
    score: z.null(),
    factors: z.null(),
    confidence: z.null(),
    factorEvidence: z.null(),
    jiraAction: z.null(),
  });

export const contentIntelligenceTicketQualityAssessmentSchema = z.discriminatedUnion('status', [
  contentIntelligenceTicketQualityAssessedAssessmentSchema,
  contentIntelligenceTicketQualitySourceUnavailableAssessmentSchema,
]);

export const contentIntelligenceTicketQualityResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    truncated: z.boolean(),
    items: z
      .array(contentIntelligenceTicketQualityAssessmentSchema)
      .max(MAX_CONTENT_INTELLIGENCE_TICKET_QUALITY_LIMIT),
    dataSource: z.literal('sir-ticket-quality'),
  })
  .superRefine((response, context) => {
    if (response.returned !== response.items.length) {
      context.addIssue({
        code: 'custom',
        message: 'Returned count must equal the number of assessment items',
        path: ['returned'],
      });
    }
    if (response.total < response.returned) {
      context.addIssue({
        code: 'custom',
        message: 'Total count cannot be lower than the returned count',
        path: ['total'],
      });
    }

    const shouldBeTruncated = response.total > response.returned;
    if (response.truncated !== shouldBeTruncated) {
      context.addIssue({
        code: 'custom',
        message: 'Truncated must report whether more assessments are available',
        path: ['truncated'],
      });
    }
    for (const [index, assessment] of response.items.entries()) {
      if (assessment.loopId !== response.loopId) {
        context.addIssue({
          code: 'custom',
          message: 'Assessment loop must match the requested loop',
          path: ['items', index, 'loopId'],
        });
      }
    }
  });

export type ContentIntelligenceTicketQualityFactors = z.infer<
  typeof contentIntelligenceTicketQualityFactorsSchema
>;
export type ContentIntelligenceTicketQualityFactorEvidence = z.infer<
  typeof contentIntelligenceTicketQualityFactorEvidenceSchema
>;
export type ContentIntelligenceTicketQualityJiraAction = z.infer<
  typeof contentIntelligenceTicketQualityJiraActionSchema
>;
export type ContentIntelligenceTicketQualityAssessment = z.infer<
  typeof contentIntelligenceTicketQualityAssessmentSchema
>;
export type ContentIntelligenceTicketQualityResponse = z.infer<
  typeof contentIntelligenceTicketQualityResponseSchema
>;
